import { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/store";
import { selectClusterList } from "../../store/slices/clusterListSlice";
import {
  increaseComplexOrderCartQuantity,
  decreaseComplexOrderCartQuantity,
  removeFromComplexOrderCart,
  removeAllByParentId,
  clearComplexOrderCart,
} from "../../store/slices/complexOrderCartSlice";
import { ComplexOrderItem } from "../../common/types/interfaces";

export interface ComplexClusterGroup {
  clusterId: number;
  cluster: any;
  items: ComplexOrderItem[];
  total: number;
}

export interface FieldsGroup {
  categoryId: number;
  categoryName: string;
  fields: any[];
}

export const useComplexCart = () => {
  const dispatch = useDispatch();
  const items = useSelector((s: RootState) => s.complexOrderCart.items);
  const clusters = useSelector(selectClusterList);

  useEffect(() => {
    console.log("=== COMPLEX CART ITEMS ===", items);
  }, [items]);

  const grouped: ComplexClusterGroup[] = useMemo(() => {
    const map = new Map<number, ComplexClusterGroup>();

    items.forEach((item) => {
      if (!map.has(item.clusterId)) {
        map.set(item.clusterId, {
          clusterId: item.clusterId,
          cluster: clusters.find((c) => c.id === item.clusterId),
          items: [],
          total: 0,
        });
      }
      const group = map.get(item.clusterId)!;
      group.items.push(item);
    });

    // Считаем total: родители с ценой + дети родителей без цены
    for (const group of Array.from(map.values())) {
      let total = 0;

      for (const item of group.items) {
        if (!item.parentId) {
          total += item.total;
        } else {
          const parent = group.items.find((i) => i.id === item.parentId);
          if (!parent || parent.price <= 0) {
            total += item.total;
          }
        }
      }

      group.total = total;
    }

    return Array.from(map.values());
  }, [items, clusters]);

  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(
    grouped.length === 1 ? grouped[0].clusterId : null,
  );

  useEffect(() => {
    if (grouped.length === 1) {
      setSelectedClusterId(grouped[0].clusterId);
    } else if (
      selectedClusterId !== null &&
      !grouped.some((g) => g.clusterId === selectedClusterId)
    ) {
      setSelectedClusterId(null);
    }
  }, [grouped, selectedClusterId]);

  const selectedGroup = grouped.find((g) => g.clusterId === selectedClusterId);

  const groupedItems = useMemo(() => {
    if (!selectedGroup) return { parents: [], childrenMap: {} };

    const parents: ComplexOrderItem[] = [];
    const childrenMap: Record<string, ComplexOrderItem[]> = {};

    selectedGroup.items.forEach((item) => {
      if (!item.parentId) {
        parents.push(item);
      } else {
        if (!childrenMap[item.parentId]) childrenMap[item.parentId] = [];
        childrenMap[item.parentId].push(item);
      }
    });

    if (parents.length === 0 && Object.keys(childrenMap).length > 0) {
      return { parents: Object.values(childrenMap).flat(), childrenMap: {} };
    }

    console.log("=== GROUPED ===", { parents, childrenMap });

    return { parents, childrenMap };
  }, [selectedGroup]);

  const fieldsByCategory: FieldsGroup[] = useMemo(() => {
    if (!selectedGroup) return [];

    const result = new Map<number, FieldsGroup>();

    selectedGroup.items.forEach((item) => {
      const cat = (item as any).category;
      if (!cat?.isComplex || !cat.fields?.length) return;

      if (!result.has(cat.id)) {
        result.set(cat.id, {
          categoryId: cat.id,
          categoryName: cat.name,
          fields: [],
        });
      }
      const group = result.get(cat.id)!;
      cat.fields.forEach((f: any) => {
        if (f.isActive && f.isVisibleForClient) {
          if (!group.fields.find((x) => x.id === f.id)) {
            group.fields.push(f);
          }
        }
      });
    });

    return Array.from(result.values()).sort(
      (a, b) => a.categoryId - b.categoryId,
    );
  }, [selectedGroup]);

  const increase = useCallback(
    (id: string) => dispatch(increaseComplexOrderCartQuantity(id)),
    [dispatch],
  );
  const decrease = useCallback(
    (id: string) => dispatch(decreaseComplexOrderCartQuantity(id)),
    [dispatch],
  );
  const remove = useCallback(
    (id: string) => dispatch(removeFromComplexOrderCart(id)),
    [dispatch],
  );
  const removeAll = useCallback(
    (id: string) => dispatch(removeAllByParentId(id)),
    [dispatch],
  );
  const clear = useCallback(() => dispatch(clearComplexOrderCart()), [dispatch]);

  return {
    grouped,
    selectedClusterId,
    setSelectedClusterId,
    selectedGroup,
    groupedItems,
    fieldsByCategory,
    isEmpty: grouped.length === 0,
    increase,
    decrease,
    remove,
    removeAll,
    clear,
  };
};